import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  FormControlLabel,
  Switch,
  FormHelperText,
  useMediaQuery,
  Divider,
  Tabs,
  Tab,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PersonAddAlt1 as PersonAddAlt1Icon,
  Search as SearchIcon,
  Storage as StorageIcon,
  Tune as TuneIcon,
  Security as SecurityIcon,
  VpnKey as VpnKeyIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { alpha, useTheme } from '@mui/material/styles';
import { useComponentWebSocket } from './GlobalSettings/GlobalSettingUtils';
import CloseButton from './Common/CloseButton';
import {
  emptyPermissionScope,
  getStorageAccessFlags,
  normalizeIdList,
  normalizePermissionScope,
  setStorageAccessFlags,
  scopeHasAnyEntry
} from '../Utils/permissionScope';

const AccountManagement = ({ userPermission, currentUsername }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobileDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const primaryColor = '#5757b0';
  const hoverColor = '#7777d0';

  const [users, setUsers] = useState([]);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [scopePickerOpen, setScopePickerOpen] = useState(false);
  const [scopePickerTab, setScopePickerTab] = useState('projects');
  const [scopeSearchQuery, setScopeSearchQuery] = useState('');
  const [scopePageByTab, setScopePageByTab] = useState({ projects: 0, collections: 0 });
  const [scopeRowsPerPage, setScopeRowsPerPage] = useState(25);
  const [scopeDraft, setScopeDraft] = useState(emptyPermissionScope);
  const [scopeTotals, setScopeTotals] = useState({ projects: 0, collections: 0 });
  const [editingUsername, setEditingUsername] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [availableCollections, setAvailableCollections] = useState([]);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    permission: 'view_only',
    permissionScope: emptyPermissionScope,
    api_enabled: false,
    first_name: '',
    last_name: '',
    email: ''
  });
  const [generatedApiKey, setGeneratedApiKey] = useState({
    open: false,
    username: '',
    token: '',
    keyPrefix: '',
    createdAt: ''
  });
  const [loading, setLoading] = useState(false);
  const [globalMfaRequired, setGlobalMfaRequired] = useState(false);
  const [globalMfaLoading, setGlobalMfaLoading] = useState(false);
  const [globalMfaError, setGlobalMfaError] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const globalMfaPrevRef = useRef(false);

  const dialogFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      '& fieldset': {
        borderColor: 'rgba(87, 87, 176, 0.2)'
      },
      '&:hover fieldset': {
        borderColor: hoverColor
      },
      '&.Mui-focused fieldset': {
        borderColor: primaryColor,
        boxShadow: '0 0 0 3px rgba(87, 87, 176, 0.1)'
      }
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: primaryColor
    }
  };

  // Define command handlers for WebSocket messages
  const commandHandlers = {
    'get_all_users': (message) => {
      if (message.data.status === 'success') {
        setUsers(message.data.users || []);
      } else {
        showSnackbar(message.data.message || 'Failed to load users', 'error');
      }
      setLoading(false);
    },
    'add_user': (message) => {
      if (message.data.status === 'success') {
        showSnackbar(message.data.message || 'User added successfully', 'success');
        setAddUserOpen(false);
        setScopePickerOpen(false);
        setScopeSearchQuery('');
        setEditingUsername('');
        setNewUser({
          username: '',
          password: '',
          permission: 'view_only',
          permissionScope: emptyPermissionScope,
          api_enabled: false,
          first_name: '',
          last_name: '',
          email: ''
        });
        loadUsers();
      } else {
        showSnackbar(message.data.message || 'Failed to add user', 'error');
      }
      setLoading(false);
    },
    'update_user': (message) => {
      if (message.data.status === 'success') {
        showSnackbar(message.data.message || 'User updated successfully', 'success');
        setAddUserOpen(false);
        setScopePickerOpen(false);
        setScopeSearchQuery('');
        setEditingUsername('');
        setNewUser({
          username: '',
          password: '',
          permission: 'view_only',
          permissionScope: emptyPermissionScope,
          api_enabled: false,
          first_name: '',
          last_name: '',
          email: ''
        });
        loadUsers();
      } else {
        showSnackbar(message.data.message || 'Failed to update user', 'error');
      }
      setLoading(false);
    },
    'delete_user': (message) => {
      if (message.data.status === 'success') {
        showSnackbar(message.data.message || 'User deleted successfully', 'success');
        setDeleteConfirmOpen(false);
        setUserToDelete(null);
        loadUsers();
      } else {
        showSnackbar(message.data.message || 'Failed to delete user', 'error');
      }
      setLoading(false);
    },
    'users_updated': (message) => {
      setUsers(message.data.users || []);
    },
    'generate_user_api_key': (message) => {
      setLoading(false);
      if (message.data.status === 'success' && message.data.key?.token) {
        setGeneratedApiKey({
          open: true,
          username: message.data.key.username || '',
          token: message.data.key.token || '',
          keyPrefix: message.data.key.key_prefix || '',
          createdAt: message.data.key.created_at || ''
        });
        showSnackbar(message.data.message || t('account_management.api_key_generated_success'), 'success');
      } else {
        showSnackbar(message.data.message || t('account_management.api_key_generated_error'), 'error');
      }
    },
    'get_global_mfa_requirement': (message) => {
      if (message.data?.success) {
        setGlobalMfaRequired(!!message.data.required);
        setGlobalMfaError('');
      } else {
        const errorMessage = message.data?.message || t('account_settings.global_mfa_load_failed');
        setGlobalMfaError(errorMessage);
        showSnackbar(errorMessage, 'error');
      }
    },
    'set_global_mfa_requirement': (message) => {
      setGlobalMfaLoading(false);
      if (message.data?.success) {
        setGlobalMfaRequired(!!message.data.required);
        setGlobalMfaError('');
        showSnackbar(message.data?.message || t('account_settings.global_mfa_updated'), 'success');
      } else {
        const errorMessage = message.data?.message || t('account_settings.global_mfa_update_failed');
        setGlobalMfaRequired(globalMfaPrevRef.current);
        setGlobalMfaError(errorMessage);
        showSnackbar(errorMessage, 'error');
      }
    },
    'listcases': (message) => {
      const payload = message?.data || {};
      if (!payload.scope_picker) {
        return;
      }
      const cases = Array.isArray(message?.data?.cases) ? message.data.cases : [];
      const mappedProjects = cases.map((project) => ({
        id: Number(project.id),
        title: project.title || `#${project.id}`
      })).filter((project) => Number.isInteger(project.id) && project.id > 0);
      setAvailableProjects(mappedProjects);
      const total = Number(payload.total_count);
      setScopeTotals((prev) => ({
        ...prev,
        projects: Number.isInteger(total) && total >= 0 ? total : mappedProjects.length
      }));
      const responsePage = Number(payload.page);
      if (Number.isInteger(responsePage) && responsePage >= 0) {
        setScopePageByTab((prev) => ({ ...prev, projects: responsePage }));
      }
    },
    'getAllCollections': (message) => {
      const payload = message?.data || {};
      if (!payload.scope_picker) {
        return;
      }
      const collections = Array.isArray(message?.data?.collections) ? message.data.collections : [];
      const mappedCollections = collections.map((collection) => ({
        id: Number(collection.id),
        title: collection.title || `#${collection.id}`
      })).filter((collection) => Number.isInteger(collection.id) && collection.id > 0);
      setAvailableCollections(mappedCollections);
      const total = Number(payload.total_count);
      setScopeTotals((prev) => ({
        ...prev,
        collections: Number.isInteger(total) && total >= 0 ? total : mappedCollections.length
      }));
      const responsePage = Number(payload.page);
      if (Number.isInteger(responsePage) && responsePage >= 0) {
        setScopePageByTab((prev) => ({ ...prev, collections: responsePage }));
      }
    }
  };

  const { sendCommand } = useComponentWebSocket(commandHandlers);

  const loadUsers = useCallback(() => {
    if (userPermission !== 'admin') return;
    
    setLoading(true);
    sendCommand('get_all_users', {});
  }, [userPermission, sendCommand]);

  useEffect(() => {
    if (userPermission === 'admin') {
      loadUsers();
    }
  }, [userPermission, loadUsers]);

  useEffect(() => {
    if (userPermission !== 'admin') {
      setGlobalMfaLoading(false);
      setGlobalMfaError('');
      return;
    }
    sendCommand('get_global_mfa_requirement', {});
  }, [userPermission, sendCommand]);

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleGlobalMfaToggle = (event) => {
    const nextValue = event.target.checked;
    globalMfaPrevRef.current = globalMfaRequired;
    setGlobalMfaRequired(nextValue);
    setGlobalMfaLoading(true);
    setGlobalMfaError('');
    sendCommand('set_global_mfa_requirement', { required: nextValue });
  };

  const requestScopeItems = (resource, overrides = {}) => {
    if (userPermission !== 'admin') return;
    if (resource !== 'projects' && resource !== 'collections') return;

    const page = Number.isInteger(overrides.page) && overrides.page >= 0
      ? overrides.page
      : Math.max(0, scopePageByTab[resource] || 0);
    const limit = Number.isInteger(overrides.limit) && overrides.limit > 0
      ? overrides.limit
      : scopeRowsPerPage;
    const search = typeof overrides.search === 'string'
      ? overrides.search
      : scopeSearchQuery;

    const sourceScope = scopePickerOpen
      ? normalizePermissionScope(scopeDraft)
      : normalizePermissionScope(newUser.permissionScope);
    const resourceScope = sourceScope[resource] || { read: [], upload: [], edit: [] };
    const selectedIds = Array.isArray(overrides.selected_ids)
      ? normalizeIdList(overrides.selected_ids)
      : [...new Set([
          ...(resourceScope.read || []),
          ...(resourceScope.upload || []),
          ...(resourceScope.edit || [])
        ])];

    const payload = {
      scope_picker: true,
      page,
      limit,
      search,
      selected_ids: selectedIds
    };

    if (resource === 'projects') {
      sendCommand('getcases', payload);
      return;
    }
    sendCommand('getAllCollections', payload);
  };

  useEffect(() => {
    if (!addUserOpen || userPermission !== 'admin') return;
    requestScopeItems('projects', { page: 0, search: '' });
    requestScopeItems('collections', { page: 0, search: '' });
  }, [addUserOpen, userPermission]);

  const closeAddUserDialog = () => {
    setAddUserOpen(false);
    setScopePickerOpen(false);
    setEditingUsername('');
  };

  const handlePermissionChange = (permissionValue) => {
    setNewUser((prev) => ({
      ...prev,
      permission: permissionValue
    }));
    if (permissionValue !== 'scoped') {
      setScopePickerOpen(false);
    }
  };

  const isEditingUser = Boolean(editingUsername);

  const openAddUserDialog = () => {
    setEditingUsername('');
    setScopePickerOpen(false);
    setScopeSearchQuery('');
    setScopeTotals({ projects: 0, collections: 0 });
    setAvailableProjects([]);
    setAvailableCollections([]);
    setNewUser({
      username: '',
      password: '',
      permission: 'view_only',
      permissionScope: emptyPermissionScope,
      api_enabled: false,
      first_name: '',
      last_name: '',
      email: ''
    });
    setAddUserOpen(true);
  };

  const openEditUserDialog = (userRow) => {
    const username = (userRow?.username || '').trim();
    if (!username || userRow?.is_system_user) return;
    setEditingUsername(username);
    setScopePickerOpen(false);
    setScopeSearchQuery('');
    setScopeTotals({ projects: 0, collections: 0 });
    setAvailableProjects([]);
    setAvailableCollections([]);
    setNewUser({
      username,
      password: '',
      permission: userRow?.permission || 'view_only',
      permissionScope: normalizePermissionScope(userRow?.permission_scope),
      api_enabled: Boolean(userRow?.api_enabled),
      first_name: userRow?.first_name || '',
      last_name: userRow?.last_name || '',
      email: userRow?.email || ''
    });
    setAddUserOpen(true);
  };

  const handleSaveUser = () => {
    if (!newUser.username.trim()) {
      showSnackbar('Username is required', 'error');
      return;
    }

    if (newUser.username.length < 3) {
      showSnackbar('Username must be at least 3 characters long', 'error');
      return;
    }

    if (!isEditingUser && !newUser.password.trim()) {
      showSnackbar('Username and password are required', 'error');
      return;
    }

    if (!isEditingUser && newUser.password.length < 6) {
      showSnackbar('Password must be at least 6 characters long', 'error');
      return;
    }

    if (isEditingUser && newUser.password.trim() && newUser.password.length < 6) {
      showSnackbar('Password must be at least 6 characters long', 'error');
      return;
    }

    if (newUser.permission === 'scoped' && !scopeHasAnyEntry(newUser.permissionScope)) {
      showSnackbar(t('account_management.scoped_permission_required'), 'error');
      return;
    }

    setLoading(true);
    const firstNameValue = typeof newUser.first_name === 'string' ? newUser.first_name.trim() : '';
    const lastNameValue = typeof newUser.last_name === 'string' ? newUser.last_name.trim() : '';
    const emailValue = typeof newUser.email === 'string' ? newUser.email.trim().toLowerCase() : '';
    const payload = {
      username: newUser.username.trim(),
      permission: newUser.permission,
      api_enabled: !!newUser.api_enabled,
      first_name: firstNameValue,
      last_name: lastNameValue,
      email: emailValue
    };

    if (isEditingUser) {
      if (newUser.password.trim()) {
        payload.password = newUser.password;
      }
      if (newUser.permission === 'scoped') {
        payload.permission_scope = normalizePermissionScope(newUser.permissionScope);
      }
      sendCommand('update_user', payload);
      return;
    }

    payload.password = newUser.password;
    if (newUser.permission === 'scoped') {
      payload.permission_scope = normalizePermissionScope(newUser.permissionScope);
    }
    sendCommand('add_user', payload);
  };

  const handleDeleteUser = (username) => {
    setUserToDelete(username);
    setDeleteConfirmOpen(true);
  };

  const handleGenerateApiKey = (username) => {
    if (!username) return;
    setLoading(true);
    sendCommand('generate_user_api_key', { username });
  };

  const handleCloseGeneratedApiKey = () => {
    setGeneratedApiKey({
      open: false,
      username: '',
      token: '',
      keyPrefix: '',
      createdAt: ''
    });
  };

  const handleCopyGeneratedApiKey = async () => {
    if (!generatedApiKey.token) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(generatedApiKey.token);
        showSnackbar(t('account_management.api_key_copied'), 'success');
        return;
      }
      showSnackbar(t('account_management.api_key_copy_unavailable'), 'warning');
    } catch (error) {
      showSnackbar(t('account_management.api_key_copy_failed'), 'error');
    }
  };

  const confirmDeleteUser = () => {
    if (!userToDelete) return;

    setLoading(true);
    sendCommand('delete_user', {
      username: userToDelete
    });
  };

  const getPermissionChip = (permission) => {
    const colors = {
      admin: 'error',
      editor: 'warning',
      view_only: 'default',
      scoped: 'info'
    };

    const labels = {
      admin: t('account_management.admin'),
      editor: t('account_management.editor'),
      view_only: t('account_management.view_only'),
      scoped: t('account_management.scoped')
    };

    return (
      <Chip
        label={labels[permission] || permission}
        color={colors[permission] || 'default'}
        size="small"
      />
    );
  };

  const getApiAccessChip = (isEnabled) => (
    <Chip
      label={isEnabled
        ? t('account_management.api_enabled')
        : t('account_management.api_disabled')}
      color={isEnabled ? 'success' : 'default'}
      variant={isEnabled ? 'filled' : 'outlined'}
      size="small"
    />
  );

  const getUserFullName = (userRow) => {
    const parts = [userRow?.first_name, userRow?.last_name].filter(Boolean);
    return parts.length ? parts.join(' ') : '-';
  };

  const getScopeCounts = (scope) => {
    const normalized = normalizePermissionScope(scope);
    return {
      projectRead: normalized.projects.read.length,
      projectUpload: normalized.projects.upload.length,
      projectEdit: normalized.projects.edit.length,
      collectionRead: normalized.collections.read.length,
      collectionUpload: normalized.collections.upload.length,
      collectionEdit: normalized.collections.edit.length
    };
  };

  const renderScopeSummaryChips = (scope) => {
    const counts = getScopeCounts(scope);

    const metricBadgeSx = (active, color) => ({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 0.6,
      py: 0.18,
      borderRadius: '999px',
      fontSize: '0.66rem',
      fontWeight: 700,
      lineHeight: 1.35,
      minWidth: '34px',
      color: active ? color : alpha(theme.palette.text.secondary, 0.95),
      border: `1px solid ${active ? alpha(color, 0.34) : alpha(theme.palette.divider, 0.82)}`,
      backgroundColor: active ? alpha(color, 0.12) : alpha(theme.palette.action.hover, 0.35),
      transition: 'background-color .18s ease, border-color .18s ease, color .18s ease'
    });

    const metricBadge = (metricKey, count, color, description) => (
      <Tooltip
        key={metricKey}
        title={description}
        arrow
        enterDelay={280}
      >
        <Box component="span" sx={{ ...metricBadgeSx(count > 0, color), cursor: 'help' }}>
          {metricKey} {count}
        </Box>
      </Tooltip>
    );

    const resourceChip = (label, shortLabel, readCount, uploadCount, editCount, color) => (
      <Tooltip
        key={shortLabel}
        arrow
        enterDelay={260}
        title={t('account_management.scope_chip_tooltip', {
          defaultValue: '{{label}} access scope. Read: {{read}}. Upload: {{upload}}. Edit: {{edit}}.',
          label,
          read: readCount,
          upload: uploadCount,
          edit: editCount
        })}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
            px: 0.9,
            py: 0.45,
            borderRadius: '999px',
            border: `1px solid ${alpha(theme.palette.divider, 0.95)}`,
            backgroundColor: alpha(theme.palette.background.paper, 0.96),
            boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}`,
            transition: 'background-color .2s ease, border-color .2s ease, box-shadow .2s ease',
            cursor: 'help',
            '&:hover': {
              backgroundColor: alpha(theme.palette.action.hover, 0.62),
              borderColor: alpha(color, 0.38),
              boxShadow: `0 2px 6px ${alpha(theme.palette.common.black, 0.12)}`
            }
          }}
        >
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '20px',
              height: '20px',
              borderRadius: '999px',
              fontSize: '0.68rem',
              fontWeight: 800,
              letterSpacing: 0.2,
              color: alpha(color, 0.95),
              backgroundColor: alpha(color, 0.12),
              border: `1px solid ${alpha(color, 0.3)}`
            }}
          >
            {shortLabel}
          </Box>
          {metricBadge(
            'R',
            readCount,
            theme.palette.info.main,
            t('account_management.scope_read_tooltip', {
              defaultValue: '{{type}} read access: {{count}}',
              type: label,
              count: readCount
            })
          )}
          {metricBadge(
            'E',
            editCount,
            theme.palette.success.main,
            t('account_management.scope_edit_tooltip', {
              defaultValue: '{{type}} edit access: {{count}}',
              type: label,
              count: editCount
            })
          )}
          {metricBadge(
            'U',
            uploadCount,
            theme.palette.warning.main,
            t('account_management.scope_upload_tooltip', {
              defaultValue: '{{type}} upload access: {{count}}',
              type: label,
              count: uploadCount
            })
          )}
        </Box>
      </Tooltip>
    );

    const storageAccess = getStorageAccessFlags(scope);
    const storageChip = (
      <Tooltip
        key="storage"
        arrow
        enterDelay={260}
        title={t('account_management.storage_scope_tooltip', {
          defaultValue: 'Storage access. Read: {{read}}. Write: {{write}}.',
          read: storageAccess.read ? 1 : 0,
          write: storageAccess.write ? 1 : 0
        })}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
            px: 0.9,
            py: 0.45,
            borderRadius: '999px',
            border: `1px solid ${alpha(theme.palette.divider, 0.95)}`,
            backgroundColor: alpha(theme.palette.background.paper, 0.96),
            boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}`,
            transition: 'background-color .2s ease, border-color .2s ease, box-shadow .2s ease',
            cursor: 'help',
            '&:hover': {
              backgroundColor: alpha(theme.palette.action.hover, 0.62),
              borderColor: alpha(theme.palette.success.main, 0.38),
              boxShadow: `0 2px 6px ${alpha(theme.palette.common.black, 0.12)}`
            }
          }}
        >
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '20px',
              height: '20px',
              borderRadius: '999px',
              fontSize: '0.68rem',
              fontWeight: 800,
              letterSpacing: 0.2,
              color: alpha(theme.palette.success.main, 0.95),
              backgroundColor: alpha(theme.palette.success.main, 0.12),
              border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
            }}
          >
            S
          </Box>
          {metricBadge(
            'R',
            storageAccess.read ? 1 : 0,
            theme.palette.info.main,
            t('account_management.storage_read_tooltip', {
              defaultValue: 'Storage read access: {{count}}',
              count: storageAccess.read ? 1 : 0
            })
          )}
          {metricBadge(
            'W',
            storageAccess.write ? 1 : 0,
            theme.palette.warning.main,
            t('account_management.storage_write_tooltip', {
              defaultValue: 'Storage write access: {{count}}',
              count: storageAccess.write ? 1 : 0
            })
          )}
        </Box>
      </Tooltip>
    );

    return (
      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
        {resourceChip(
          t('account_management.projects', 'Projects'),
          'P',
          counts.projectRead,
          counts.projectUpload,
          counts.projectEdit,
          theme.palette.primary.main
        )}
        {resourceChip(
          t('account_management.collections', 'Collections'),
          'C',
          counts.collectionRead,
          counts.collectionUpload,
          counts.collectionEdit,
          theme.palette.secondary.main
        )}
        {storageChip}
      </Stack>
    );
  };

  const normalizedScope = normalizePermissionScope(newUser.permissionScope);
  const normalizedDraftScope = normalizePermissionScope(scopeDraft);
  const scopeStorageAccess = getStorageAccessFlags(normalizedScope);
  const draftStorageAccess = getStorageAccessFlags(normalizedDraftScope);
  const activeScopeItems = scopePickerTab === 'projects' ? availableProjects : availableCollections;
  const activeTabReadSet = new Set(normalizedDraftScope[scopePickerTab].read);
  const activeTabUploadSet = new Set(normalizedDraftScope[scopePickerTab].upload);
  const activeTabEditSet = new Set(normalizedDraftScope[scopePickerTab].edit);
  const normalizedSearch = (scopeSearchQuery || '').trim().toLowerCase();
  const filteredScopeItems = activeScopeItems
    .filter((item) => {
      const title = (item?.title || '').toLowerCase();
      const idText = String(item?.id || '');
      return title.includes(normalizedSearch) || idText.includes(normalizedSearch);
    })
    .sort((a, b) => {
      const aId = Number(a?.id);
      const bId = Number(b?.id);

      const aHasEdit = activeTabEditSet.has(aId);
      const bHasEdit = activeTabEditSet.has(bId);
      if (aHasEdit !== bHasEdit) return aHasEdit ? -1 : 1;

      const aHasUpload = activeTabUploadSet.has(aId);
      const bHasUpload = activeTabUploadSet.has(bId);
      if (aHasUpload !== bHasUpload) return aHasUpload ? -1 : 1;

      const aHasRead = activeTabReadSet.has(aId);
      const bHasRead = activeTabReadSet.has(bId);
      if (aHasRead !== bHasRead) return aHasRead ? -1 : 1;

      const aTitle = (a?.title || '').trim();
      const bTitle = (b?.title || '').trim();
      const titleCompare = aTitle.localeCompare(bTitle, undefined, { numeric: true, sensitivity: 'base' });
      if (titleCompare !== 0) return titleCompare;

      if (Number.isFinite(aId) && Number.isFinite(bId)) return aId - bId;
      return 0;
    });
  const filteredScopeIds = filteredScopeItems.map((item) => Number(item.id)).filter((id) => Number.isInteger(id) && id > 0);
  const currentScopePage = Math.max(0, scopePageByTab[scopePickerTab] || 0);
  const activeScopeTotal = scopeTotals[scopePickerTab] ?? filteredScopeItems.length;
  const pagedScopeItems = filteredScopeItems;

  const setCurrentScopePage = (nextPage) => {
    setScopePageByTab((prev) => ({
      ...prev,
      [scopePickerTab]: Math.max(0, nextPage)
    }));
  };

  const handleScopeSearchChange = (value) => {
    setScopeSearchQuery(value);
    setScopePageByTab((prev) => ({
      ...prev,
      [scopePickerTab]: 0
    }));
    requestScopeItems(scopePickerTab, { page: 0, search: value });
  };

  const handleScopeTabChange = (value) => {
    setScopePickerTab(value);
    requestScopeItems(value, { page: scopePageByTab[value] || 0 });
  };

  const handleScopePageChange = (_event, nextPage) => {
    setCurrentScopePage(nextPage);
    requestScopeItems(scopePickerTab, { page: nextPage });
  };

  const handleScopeRowsPerPageChange = (event) => {
    const nextRowsPerPage = parseInt(event.target.value, 10);
    const normalizedRowsPerPage = Number.isInteger(nextRowsPerPage) && nextRowsPerPage > 0 ? nextRowsPerPage : 25;
    setScopeRowsPerPage(normalizedRowsPerPage);
    setScopePageByTab({ projects: 0, collections: 0 });
    requestScopeItems(scopePickerTab, { page: 0, limit: normalizedRowsPerPage });
  };

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(activeScopeTotal / scopeRowsPerPage) - 1);
    if (currentScopePage > maxPage) {
      setScopePageByTab((prev) => ({
        ...prev,
        [scopePickerTab]: maxPage
      }));
    }
  }, [activeScopeTotal, scopeRowsPerPage, currentScopePage, scopePickerTab]);

  const updateScopeDraft = (resource, updater) => {
    setScopeDraft((prev) => {
      const normalized = normalizePermissionScope(prev);
      const current = normalized[resource];
      const next = typeof updater === 'function'
        ? updater({ read: [...current.read], upload: [...current.upload], edit: [...current.edit] })
        : updater;
      return {
        ...normalized,
        [resource]: {
          read: normalizeIdList(next?.read || []),
          upload: normalizeIdList(next?.upload || []),
          edit: normalizeIdList(next?.edit || [])
        }
      };
    });
  };

  const updateStorageScopeDraft = (updater) => {
    setScopeDraft((prev) => {
      const current = getStorageAccessFlags(prev);
      const next = typeof updater === 'function'
        ? updater(current)
        : updater;
      return setStorageAccessFlags(prev, next);
    });
  };

  const handleStorageReadChange = (event) => {
    const nextRead = Boolean(event.target.checked);
    updateStorageScopeDraft((current) => ({
      read: nextRead,
      write: nextRead ? current.write : false
    }));
  };

  const handleStorageWriteChange = (event) => {
    const nextWrite = Boolean(event.target.checked);
    updateStorageScopeDraft((current) => ({
      read: nextWrite ? true : current.read,
      write: nextWrite
    }));
  };

  const toggleScopeRead = (resource, itemId) => {
    updateScopeDraft(resource, ({ read, upload, edit }) => {
      const hasRead = read.includes(itemId);
      if (hasRead) {
        return {
          read: read.filter((id) => id !== itemId),
          upload,
          edit: edit.filter((id) => id !== itemId)
        };
      }
      return {
        read: [...read, itemId],
        upload,
        edit
      };
    });
  };

  const toggleScopeUpload = (resource, itemId) => {
    updateScopeDraft(resource, ({ read, upload, edit }) => {
      const hasUpload = upload.includes(itemId);
      const hasEdit = edit.includes(itemId);
      if (hasUpload) {
        return {
          read,
          upload: upload.filter((id) => id !== itemId),
          edit: hasEdit ? edit.filter((id) => id !== itemId) : edit
        };
      }
      return {
        read,
        upload: [...upload, itemId],
        edit
      };
    });
  };

  const toggleScopeEdit = (resource, itemId) => {
    updateScopeDraft(resource, ({ read, upload, edit }) => {
      const hasEdit = edit.includes(itemId);
      if (hasEdit) {
        return {
          read,
          upload,
          edit: edit.filter((id) => id !== itemId)
        };
      }
      return {
        read: read.includes(itemId) ? read : [...read, itemId],
        upload: upload.includes(itemId) ? upload : [...upload, itemId],
        edit: [...edit, itemId]
      };
    });
  };

  const selectFilteredRead = () => {
    updateScopeDraft(scopePickerTab, ({ read, upload, edit }) => ({
      read: [...read, ...filteredScopeIds],
      upload,
      edit
    }));
  };

  const selectFilteredUpload = () => {
    updateScopeDraft(scopePickerTab, ({ read, upload, edit }) => ({
      read,
      upload: [...upload, ...filteredScopeIds],
      edit
    }));
  };

  const selectFilteredEdit = () => {
    updateScopeDraft(scopePickerTab, ({ read, upload, edit }) => ({
      read: [...read, ...filteredScopeIds],
      upload: [...upload, ...filteredScopeIds],
      edit: [...edit, ...filteredScopeIds]
    }));
  };

  const clearFiltered = () => {
    const filteredSet = new Set(filteredScopeIds);
    updateScopeDraft(scopePickerTab, ({ read, upload, edit }) => ({
      read: read.filter((id) => !filteredSet.has(id)),
      upload: upload.filter((id) => !filteredSet.has(id)),
      edit: edit.filter((id) => !filteredSet.has(id))
    }));
  };

  const clearCurrentTab = () => {
    updateScopeDraft(scopePickerTab, { read: [], upload: [], edit: [] });
  };

  const openScopePicker = () => {
    const normalized = normalizePermissionScope(newUser.permissionScope);
    setScopeDraft(normalized);
    setScopePickerTab('projects');
    setScopeSearchQuery('');
    setScopePageByTab({ projects: 0, collections: 0 });
    requestScopeItems('projects', { page: 0, search: '' });
    requestScopeItems('collections', { page: 0, search: '' });
    setScopePickerOpen(true);
  };

  const applyScopePicker = () => {
    setNewUser((prev) => ({
      ...prev,
      permissionScope: normalizePermissionScope(scopeDraft)
    }));
    setScopePickerOpen(false);
  };

  const scopedSelectedCount =
    normalizedScope.projects.read.length +
    normalizedScope.projects.upload.length +
    normalizedScope.projects.edit.length +
    normalizedScope.collections.read.length +
    normalizedScope.collections.upload.length +
    normalizedScope.collections.edit.length +
    (scopeStorageAccess.read || scopeStorageAccess.write ? 1 : 0);
  const activeTabReadCount = normalizedDraftScope[scopePickerTab].read.length;
  const activeTabUploadCount = normalizedDraftScope[scopePickerTab].upload.length;
  const activeTabEditCount = normalizedDraftScope[scopePickerTab].edit.length;

  if (userPermission !== 'admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('account_management.title')}
        </Typography>
        <Typography color="text.secondary">
          {t('account_management.admin_only')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h6">
          {t('account_management.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAddUserDialog}
          disabled={loading}
        >
          {t('account_management.add_user')}
        </Button>
      </Stack>

      <Paper
        sx={{
          p: 3,
          backgroundColor: '#fffaf2',
          borderRadius: 2,
          border: `1px solid ${primaryColor}20`,
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <SecurityIcon sx={{ color: primaryColor }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
            {t('account_settings.organization_mfa_title')}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
          {t('account_settings.organization_mfa_subtitle')}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={globalMfaRequired}
              onChange={handleGlobalMfaToggle}
              disabled={loading || globalMfaLoading}
              color="primary"
            />
          }
          label={
            globalMfaRequired
              ? t('account_settings.organization_mfa_required')
              : t('account_settings.organization_mfa_optional')
          }
        />
        {globalMfaLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" sx={{ color: '#666' }}>
              {t('account_settings.organization_mfa_updating')}
            </Typography>
          </Box>
        )}
        {globalMfaError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {globalMfaError}
          </Alert>
        )}
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('account_management.username')}</TableCell>
              <TableCell>{t('account_management.name', 'Name')}</TableCell>
              <TableCell>{t('account_management.email', 'Email')}</TableCell>
              <TableCell>{t('account_management.api_access', 'API Access')}</TableCell>
              <TableCell>{t('account_management.permission')}</TableCell>
              <TableCell width={180}>{t('account_management.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => {
              const isSystemUser = Boolean(user?.is_system_user);
              const isApiEnabled = Boolean(user?.api_enabled);
              const canGenerateApiKey = !isSystemUser && isApiEnabled;
              return (
              <TableRow key={user.id}>
                <TableCell>
                  {user.username}
                  {user.username === currentUsername && (
                    <Chip label={t('account_management.you')} size="small" sx={{ ml: 1 }} />
                  )}
                </TableCell>
                <TableCell>
                  {getUserFullName(user)}
                </TableCell>
                <TableCell>
                  {user.email || '-'}
                </TableCell>
                <TableCell>
                  {getApiAccessChip(isApiEnabled)}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    {getPermissionChip(user.permission)}
                    {user.permission === 'scoped' && (
                      renderScopeSummaryChips(user.permission_scope)
                    )}
                    {Number(user.mfa_enabled) === 1 && (
                      <Chip
                        label={t('account_management.mfa_enabled')}
                        color="success"
                        size="small"
                      />
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Tooltip
                    title={isSystemUser
                      ? t('account_management.system_user_locked', 'System user cannot be modified')
                      : isApiEnabled
                        ? t('account_management.generate_api_key')
                        : t('account_management.enable_api_before_generating')}
                  >
                    <span>
                      <IconButton
                        color="secondary"
                        onClick={() => handleGenerateApiKey(user.username)}
                        disabled={loading || !canGenerateApiKey}
                        size="small"
                      >
                        <VpnKeyIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <IconButton
                    color="primary"
                    onClick={() => openEditUserDialog(user)}
                    disabled={loading || isSystemUser}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteUser(user.username)}
                    disabled={user.username === currentUsername || loading || isSystemUser}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            )})}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add User Dialog */}
      <Dialog
        open={addUserOpen}
        onClose={closeAddUserDialog}
        fullScreen={isMobileDialog}
        scroll="paper"
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: '16px' },
            boxShadow: '0 12px 40px rgba(87, 87, 176, 0.2)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${primaryColor}, ${hoverColor})`,
            color: 'white',
            py: { xs: 1.75, sm: 2.5 },
            px: { xs: 2, sm: 3 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <PersonAddAlt1Icon />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {isEditingUser
                  ? t('account_management.edit_user', 'Edit User')
                  : t('account_management.add_new_user')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  opacity: 0.9,
                  display: { xs: 'none', sm: 'block' }
                }}
              >
                {isEditingUser
                  ? t('account_management.edit_user_subtitle', 'Update permission scope, storage access, or reset password')
                  : t('account_management.add_user_subtitle', 'Create a new account and assign permissions')}
              </Typography>
            </Box>
          </Stack>
          <CloseButton
            onClick={closeAddUserDialog}
            sx={{
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.12)' }
            }}
          />
        </DialogTitle>
        <Divider sx={{ borderColor: 'rgba(87, 87, 176, 0.14)' }} />
        <DialogContent
          sx={{
            px: { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 3 },
            pt: { xs: 5.5, sm: 6 },
            background: 'linear-gradient(180deg, rgba(247, 247, 255, 0.85) 0%, rgba(255, 255, 255, 1) 100%)',
            maxHeight: { xs: 'calc(100vh - 170px)', sm: '72vh' },
            overflowY: 'auto'
          }}
        >
          <Stack spacing={2.5}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2
              }}
            >
              <TextField
                label={t('account_management.username')}
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                fullWidth
                autoFocus
                disabled={isEditingUser}
                sx={dialogFieldSx}
              />
              <TextField
                label={isEditingUser
                  ? t('account_management.new_password_optional', 'New Password (optional)')
                  : t('account_management.password')
                }
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                fullWidth
                helperText={isEditingUser
                  ? t('account_management.leave_blank_keep_password', 'Leave blank to keep current password')
                  : ''
                }
                sx={dialogFieldSx}
              />
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2
              }}
            >
              <TextField
                label={t('account_management.first_name', 'First Name')}
                value={newUser.first_name}
                onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                fullWidth
                sx={dialogFieldSx}
              />
              <TextField
                label={t('account_management.last_name', 'Last Name')}
                value={newUser.last_name}
                onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                fullWidth
                sx={dialogFieldSx}
              />
            </Box>
            <TextField
              label={t('account_management.email', 'Email')}
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              fullWidth
              sx={dialogFieldSx}
            />
            <FormControl fullWidth>
              <InputLabel>{t('account_management.permission')}</InputLabel>
              <Select
                value={newUser.permission}
                onChange={(e) => handlePermissionChange(e.target.value)}
                label={t('account_management.permission')}
                sx={dialogFieldSx}
              >
                <MenuItem value="view_only">{t('account_management.view_only')}</MenuItem>
                <MenuItem value="editor">{t('account_management.editor')}</MenuItem>
                <MenuItem value="admin">{t('account_management.admin')}</MenuItem>
                <MenuItem value="scoped">{t('account_management.scoped')}</MenuItem>
              </Select>
            </FormControl>
            <Box
              sx={{
                border: '1px solid rgba(87, 87, 176, 0.2)',
                borderRadius: '12px',
                backgroundColor: 'rgba(240, 240, 255, 0.55)',
                p: 2
              }}
            >
              <Stack spacing={0.75}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={!!newUser.api_enabled}
                      onChange={(e) => setNewUser({
                        ...newUser,
                        api_enabled: e.target.checked
                      })}
                      color="primary"
                    />
                  )}
                  label={newUser.api_enabled
                    ? t('account_management.api_enabled')
                    : t('account_management.api_disabled')}
                />
                <FormHelperText sx={{ mt: 0, ml: 0 }}>
                  {t('account_management.api_access_help')}
                </FormHelperText>
              </Stack>
            </Box>
            {newUser.permission === 'scoped' && (
              <Box
                sx={{
                  border: '1px solid rgba(87, 87, 176, 0.2)',
                  borderRadius: '12px',
                  backgroundColor: 'rgba(240, 240, 255, 0.65)',
                  p: 2.5
                }}
              >
                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={{ xs: 1, sm: 0 }}
                  >
                    <Typography variant="subtitle2" sx={{ color: primaryColor, fontWeight: 700 }}>
                      {t('account_management.scoped_permissions')}
                    </Typography>
                    <Chip
                      size="small"
                      color={scopedSelectedCount > 0 ? 'info' : 'default'}
                      variant={scopedSelectedCount > 0 ? 'filled' : 'outlined'}
                      label={t('account_management.selected_count', {
                        count: scopedSelectedCount,
                        defaultValue: '{{count}} selected'
                      })}
                    />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                    <Box>
                      {renderScopeSummaryChips(newUser.permissionScope)}
                    </Box>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t('account_management.scope_available_summary', {
                        defaultValue: '{{projects}} projects, {{collections}} collections available',
                        projects: scopeTotals.projects,
                        collections: scopeTotals.collections
                      })}
                    />
                  </Stack>
                  <Button
                    variant="outlined"
                    startIcon={<TuneIcon />}
                    onClick={openScopePicker}
                    sx={{
                      alignSelf: 'flex-start',
                      borderColor: 'rgba(87, 87, 176, 0.35)',
                      color: 'rgba(87, 87, 176, 0.95)',
                      textTransform: 'none',
                      borderRadius: '8px',
                      '&:hover': {
                        borderColor: hoverColor,
                        backgroundColor: 'rgba(87, 87, 176, 0.06)'
                      }
                    }}
                  >
                    {t('account_management.configure_scope_access', 'Configure Access')}
                  </Button>
                  <FormHelperText sx={{ mt: 0, ml: 0 }}>
                    {t('account_management.scoped_permissions_help', 'Select which projects, collections, or storage actions this user can access.')}
                  </FormHelperText>
                  <Box
                    sx={{
                      border: '1px solid rgba(87, 87, 176, 0.16)',
                      borderRadius: '12px',
                      backgroundColor: 'rgba(255, 255, 255, 0.72)',
                      p: 2
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={1}
                      >
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <StorageIcon sx={{ color: primaryColor }} />
                          <Box>
                            <Typography variant="subtitle2" sx={{ color: primaryColor, fontWeight: 700 }}>
                              {t('account_management.storage_access', 'Storage access')}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {t('account_management.storage_access_help', 'Read allows browsing and downloading storage. Write allows creating, renaming, deleting, and uploading files. Write also grants read. Access still respects the restricted storage root.')}
                            </Typography>
                          </Box>
                        </Stack>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t('account_management.storage_scope_summary', {
                            defaultValue: 'Read {{read}} / Write {{write}}',
                            read: draftStorageAccess.read ? 1 : 0,
                            write: draftStorageAccess.write ? 1 : 0
                          })}
                        />
                      </Stack>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        useFlexGap
                        flexWrap="wrap"
                      >
                        <FormControlLabel
                          control={(
                            <Switch
                              checked={draftStorageAccess.read}
                              onChange={handleStorageReadChange}
                              color="primary"
                            />
                          )}
                          label={t('account_management.storage_read_access', 'Storage read')}
                        />
                        <FormControlLabel
                          control={(
                            <Switch
                              checked={draftStorageAccess.write}
                              onChange={handleStorageWriteChange}
                              color="primary"
                            />
                          )}
                          label={t('account_management.storage_write_access', 'Storage write')}
                        />
                      </Stack>
                      <FormHelperText sx={{ mt: 0, ml: 0 }}>
                        {t('account_management.storage_scope_help', 'Write access always includes read access. Storage actions remain limited to the configured restricted root.')}
                      </FormHelperText>
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 3 },
            pt: { xs: 1.5, sm: 2 },
            gap: 1.5,
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            alignItems: 'stretch',
            borderTop: '1px solid rgba(87, 87, 176, 0.12)',
            backgroundColor: 'rgba(247, 247, 255, 0.35)',
            '& > .MuiButton-root': {
              width: { xs: '100%', sm: 'auto' }
            }
          }}
        >
          <Button
            onClick={closeAddUserDialog}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              px: 2.5,
              textTransform: 'none',
              borderColor: 'rgba(87, 87, 176, 0.35)',
              color: 'rgba(87, 87, 176, 0.9)',
              '&:hover': {
                borderColor: hoverColor,
                backgroundColor: 'rgba(87, 87, 176, 0.06)'
              }
            }}
          >
            {t('account_management.cancel')}
          </Button>
          <Button
            onClick={handleSaveUser}
            variant="contained"
            startIcon={isEditingUser ? <EditIcon /> : <AddIcon />}
            disabled={loading}
            sx={{
              borderRadius: '8px',
              px: 2.5,
              textTransform: 'none',
              backgroundColor: primaryColor,
              boxShadow: '0 4px 12px rgba(87, 87, 176, 0.35)',
              '&:hover': {
                backgroundColor: hoverColor,
                boxShadow: '0 6px 16px rgba(87, 87, 176, 0.45)'
              }
            }}
          >
            {isEditingUser
              ? t('account_management.save_changes', 'Save Changes')
              : t('account_management.add_user')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={scopePickerOpen}
        onClose={() => setScopePickerOpen(false)}
        fullScreen={isMobileDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: '14px' },
            boxShadow: '0 12px 32px rgba(87, 87, 176, 0.24)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${primaryColor}, ${hoverColor})`,
            color: 'white',
            py: { xs: 1.75, sm: 2.25 },
            px: { xs: 2, sm: 3 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <TuneIcon />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {t('account_management.configure_scope_access', 'Configure Access')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {t('account_management.scope_picker_subtitle', 'Search and assign read/upload/edit by project or collection. Storage access is configured below.')}
              </Typography>
            </Box>
          </Stack>
          <CloseButton
            onClick={() => setScopePickerOpen(false)}
            sx={{
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.12)' }
            }}
          />
        </DialogTitle>
        <Divider sx={{ borderColor: 'rgba(87, 87, 176, 0.14)' }} />
        <DialogContent
          sx={{
            p: { xs: 2, sm: 3 },
            background: 'linear-gradient(180deg, rgba(247, 247, 255, 0.9) 0%, rgba(255, 255, 255, 1) 100%)'
          }}
        >
          <Stack spacing={2}>
            <Tabs
              value={scopePickerTab}
              onChange={(_, value) => handleScopeTabChange(value)}
              variant={isMobileDialog ? 'fullWidth' : 'standard'}
              sx={{
                '& .MuiTabs-indicator': { backgroundColor: primaryColor, height: 3 },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  minHeight: 40
                }
              }}
            >
              <Tab
                value="projects"
                label={t('account_management.projects_tab_count', {
                  defaultValue: 'Projects ({{count}})',
                  count: scopeTotals.projects
                })}
              />
              <Tab
                value="collections"
                label={t('account_management.collections_tab_count', {
                  defaultValue: 'Collections ({{count}})',
                  count: scopeTotals.collections
                })}
              />
            </Tabs>

            <TextField
              size="small"
              fullWidth
              value={scopeSearchQuery}
              onChange={(e) => handleScopeSearchChange(e.target.value)}
              placeholder={t('account_management.search_scope_items', 'Search by name or ID')}
              sx={dialogFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              useFlexGap
              flexWrap="wrap"
            >
              <Button
                size="small"
                variant="outlined"
                onClick={selectFilteredRead}
                disabled={filteredScopeIds.length === 0}
                sx={{ textTransform: 'none', borderRadius: '8px' }}
              >
                {t('account_management.select_filtered_read', 'Select Filtered as Read')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={selectFilteredUpload}
                disabled={filteredScopeIds.length === 0}
                sx={{ textTransform: 'none', borderRadius: '8px' }}
              >
                {t('account_management.select_filtered_upload', 'Select Filtered as Upload')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={selectFilteredEdit}
                disabled={filteredScopeIds.length === 0}
                sx={{ textTransform: 'none', borderRadius: '8px' }}
              >
                {t('account_management.select_filtered_edit', 'Select Filtered as Edit')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={clearFiltered}
                disabled={filteredScopeIds.length === 0}
                sx={{ textTransform: 'none', borderRadius: '8px' }}
              >
                {t('account_management.clear_filtered', 'Clear Filtered')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={clearCurrentTab}
                sx={{ textTransform: 'none', borderRadius: '8px' }}
              >
                {t('account_management.clear_all_tab', 'Clear All in Tab')}
              </Button>
            </Stack>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip
                size="small"
                variant="outlined"
                label={t('account_management.filtered_items_count', {
                  defaultValue: '{{count}} filtered',
                  count: activeScopeTotal
                })}
              />
              <Chip
                size="small"
                color="info"
                variant="outlined"
                label={t('account_management.scope_tab_summary', {
                  defaultValue: 'Read {{read}} / Upload {{upload}} / Edit {{edit}}',
                  read: activeTabReadCount,
                  upload: activeTabUploadCount,
                  edit: activeTabEditCount
                })}
              />
            </Stack>

            <Paper
              variant="outlined"
              sx={{
                borderRadius: '10px',
                borderColor: 'rgba(87, 87, 176, 0.2)',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto auto auto',
                  alignItems: 'center',
                  gap: 1,
                  px: { xs: 1.5, sm: 2 },
                  py: 1.25,
                  backgroundColor: 'rgba(87, 87, 176, 0.08)',
                  borderBottom: '1px solid rgba(87, 87, 176, 0.14)'
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {t('account_management.item_name', 'Name')}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'center' }}>
                  {t('account_management.read_short', 'Read')}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'center' }}>
                  {t('account_management.upload_short', 'Upload')}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'center' }}>
                  {t('account_management.edit_short', 'Edit')}
                </Typography>
              </Box>

              <List
                dense
                disablePadding
                sx={{
                  maxHeight: { xs: '42vh', sm: '48vh' },
                  overflowY: 'auto'
                }}
              >
                {filteredScopeItems.length === 0 && (
                  <ListItem sx={{ py: 3 }}>
                    <ListItemText
                      primary={t('account_management.no_items_found', 'No items found')}
                      secondary={t('account_management.try_another_search', 'Try a different search')}
                      primaryTypographyProps={{ align: 'center', color: 'text.secondary' }}
                      secondaryTypographyProps={{ align: 'center', color: 'text.disabled' }}
                    />
                  </ListItem>
                )}
                {pagedScopeItems.map((item) => {
                  const itemId = Number(item.id);
                  const hasRead = normalizedDraftScope[scopePickerTab].read.includes(itemId);
                  const hasUpload = normalizedDraftScope[scopePickerTab].upload.includes(itemId);
                  const hasEdit = normalizedDraftScope[scopePickerTab].edit.includes(itemId);

                  return (
                    <Box
                      key={`${scopePickerTab}-${itemId}`}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto auto auto',
                        alignItems: 'center',
                        gap: 1,
                        px: { xs: 1.5, sm: 2 },
                        py: 0.75,
                        borderBottom: '1px solid rgba(87, 87, 176, 0.08)',
                        '&:last-of-type': { borderBottom: 'none' },
                        '&:hover': { backgroundColor: 'rgba(87, 87, 176, 0.04)' }
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap title={item.title} sx={{ fontWeight: 500 }}>
                          {item.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          #{itemId}
                        </Typography>
                      </Box>
                      <Checkbox
                        size="small"
                        checked={hasRead}
                        onChange={() => toggleScopeRead(scopePickerTab, itemId)}
                        inputProps={{ 'aria-label': `read-${scopePickerTab}-${itemId}` }}
                      />
                      <Checkbox
                        size="small"
                        checked={hasUpload}
                        onChange={() => toggleScopeUpload(scopePickerTab, itemId)}
                        inputProps={{ 'aria-label': `upload-${scopePickerTab}-${itemId}` }}
                      />
                      <Checkbox
                        size="small"
                        checked={hasEdit}
                        onChange={() => toggleScopeEdit(scopePickerTab, itemId)}
                        inputProps={{ 'aria-label': `edit-${scopePickerTab}-${itemId}` }}
                      />
                    </Box>
                  );
                })}
              </List>
              <Divider sx={{ borderColor: 'rgba(87, 87, 176, 0.12)' }} />
              <TablePagination
                component="div"
                count={activeScopeTotal}
                page={currentScopePage}
                onPageChange={handleScopePageChange}
                rowsPerPage={scopeRowsPerPage}
                onRowsPerPageChange={handleScopeRowsPerPageChange}
                rowsPerPageOptions={[25, 50, 100]}
                labelRowsPerPage={t('account_management.rows_per_page', 'Rows per page')}
                sx={{
                  borderTop: 'none',
                  '& .MuiTablePagination-toolbar': {
                    minHeight: 48,
                    px: { xs: 1.25, sm: 2 }
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    mb: 0,
                    fontSize: '0.8rem'
                  }
                }}
              />
            </Paper>

            <FormHelperText sx={{ ml: 0 }}>
              {t('account_management.scope_picker_help', 'Edit implies read and upload. Upload does not imply read. Storage write implies storage read. Removing upload also removes edit for that item.')}
            </FormHelperText>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 3 },
            pt: 1.5,
            gap: 1.5,
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            alignItems: 'stretch',
            borderTop: '1px solid rgba(87, 87, 176, 0.12)',
            backgroundColor: 'rgba(247, 247, 255, 0.35)',
            '& > .MuiButton-root': {
              width: { xs: '100%', sm: 'auto' }
            }
          }}
        >
          <Button
            onClick={() => setScopePickerOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              px: 2.5,
              textTransform: 'none',
              borderColor: 'rgba(87, 87, 176, 0.35)',
              color: 'rgba(87, 87, 176, 0.9)',
              '&:hover': {
                borderColor: hoverColor,
                backgroundColor: 'rgba(87, 87, 176, 0.06)'
              }
            }}
          >
            {t('account_management.cancel')}
          </Button>
          <Button
            onClick={applyScopePicker}
            variant="contained"
            sx={{
              borderRadius: '8px',
              px: 2.5,
              textTransform: 'none',
              backgroundColor: primaryColor,
              boxShadow: '0 4px 12px rgba(87, 87, 176, 0.35)',
              '&:hover': {
                backgroundColor: hoverColor,
                boxShadow: '0 6px 16px rgba(87, 87, 176, 0.45)'
              }
            }}
          >
            {t('account_management.apply_scope_access', 'Apply Access')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{t('account_management.confirm_delete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('account_management.confirm_delete_message', { username: userToDelete })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            {t('account_management.cancel')}
          </Button>
          <Button
            onClick={confirmDeleteUser}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {t('account_management.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={generatedApiKey.open}
        onClose={handleCloseGeneratedApiKey}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('account_management.generated_api_key')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">
              {t('account_management.generated_api_key_once')}
            </Alert>
            <TextField
              label={t('account_management.username')}
              value={generatedApiKey.username}
              fullWidth
              InputProps={{ readOnly: true }}
              sx={dialogFieldSx}
            />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2
              }}
            >
              <TextField
                label={t('account_management.api_key_prefix')}
                value={generatedApiKey.keyPrefix || '-'}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={dialogFieldSx}
              />
              <TextField
                label={t('account_management.api_key_created_at')}
                value={generatedApiKey.createdAt || '-'}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={dialogFieldSx}
              />
            </Box>
            <TextField
              label={t('account_management.api_key_token')}
              value={generatedApiKey.token}
              fullWidth
              multiline
              minRows={3}
              InputProps={{ readOnly: true }}
              sx={dialogFieldSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGeneratedApiKey}>
            {t('account_management.cancel')}
          </Button>
          <Button
            onClick={handleCopyGeneratedApiKey}
            variant="contained"
            startIcon={<ContentCopyIcon />}
          >
            {t('account_management.copy_api_key')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AccountManagement;
